import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputSwitch} from "primereact/inputswitch";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import ImageExtractionDialogFields from "../../field/export/ImageExtractionDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import ImageUnit from "../../../../shared/units/ImageUnit";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Bmp,
	Functions,
	Gif,
	Information,
	Jpeg,
	Png,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	Svg,
	SvgGraphicsStateMode,
	SvgPathDrawMode,
	Tiff,
	TiffCompression,
	ToolboxImageImage
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {ImageFormat, PortalToolboxImageImage} from "../../../../shared/models/PortalToolboxImageImage";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface ImageExtractionDialogStatus extends PortalDialogState<PortalToolboxImageImage> {
	pages: number;
	currentPage: string;
}

export class ImageExtractionDialogDefinition extends PortalDialog<PortalToolboxImageImage,
	PortalDialogProperties<PortalToolboxImageImage>, ImageExtractionDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<ImageExtractionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.exportAsImage, Functions.getExportAsImageDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EXTRACT_IMAGES, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["export-images-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_images.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.extract_images.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxImageImage | undefined = this.state.parameter?.data;
		let errors: Map<ImageExtractionDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let jpegQuality: number = 80;
		let minJpegQuality: number = 1;
		let maxJpegQuality: number = 100;
		let dpi: number = 72;
		let minDpi: number = 1;
		let maxDpi: number = 9600;
		let width: number = 0;
		let minWidth: number = 0;
		let height: number = 0;
		let minHeight: number = 0;
		let metrics: string = LengthUnit.MILLIMETER;

		switch (parameter?.format) {
			case ImageFormat.JPEG:
				jpegQuality = FunctionHelper.applyValue(parameter.jpeg?.jpegQuality, Jpeg.getJpegQualityDefault());
				minJpegQuality = Jpeg.getJpegQualityMin();
				maxJpegQuality = Jpeg.getJpegQualityMax();
				dpi = FunctionHelper.applyValue(parameter.jpeg?.dpi, Jpeg.getDpiDefault());
				minDpi = Jpeg.getDpiMin();
				maxDpi = Jpeg.getDpiMax();
				width = FunctionHelper.applyValue(parameter.jpeg?.width, Jpeg.getWidthDefault());
				minWidth = Jpeg.getWidthMin();
				height = FunctionHelper.applyValue(parameter.jpeg?.height, Jpeg.getHeightDefault());
				minHeight = Jpeg.getHeightMin();
				metrics = FunctionHelper.applyValue(parameter.jpeg?.metrics, Jpeg.getMetricsDefault());
				break;
			case ImageFormat.TIFF:
				jpegQuality = FunctionHelper.applyValue(parameter.tiff?.jpegQuality, Tiff.getJpegQualityDefault());
				minJpegQuality = Tiff.getJpegQualityMin();
				maxJpegQuality = Tiff.getJpegQualityMax();
				dpi = FunctionHelper.applyValue(parameter.tiff?.dpi, Tiff.getDpiDefault());
				minDpi = Tiff.getDpiMin();
				maxDpi = Tiff.getDpiMax();
				width = FunctionHelper.applyValue(parameter.tiff?.width, Tiff.getWidthDefault());
				minWidth = Tiff.getWidthMin();
				height = FunctionHelper.applyValue(parameter.tiff?.height, Tiff.getHeightDefault());
				minHeight = Tiff.getHeightMin();
				metrics = FunctionHelper.applyValue(parameter.tiff?.metrics, Tiff.getMetricsDefault());
				break;
			case ImageFormat.BMP:
				dpi = FunctionHelper.applyValue(parameter.bmp?.dpi, Bmp.getDpiDefault());
				minDpi = Bmp.getDpiMin();
				maxDpi = Bmp.getDpiMax();
				width = FunctionHelper.applyValue(parameter.bmp?.width, Bmp.getWidthDefault());
				minWidth = Bmp.getWidthMin();
				height = FunctionHelper.applyValue(parameter.bmp?.height, Bmp.getHeightDefault());
				minHeight = Bmp.getHeightMin();
				metrics = FunctionHelper.applyValue(parameter.bmp?.metrics, Bmp.getMetricsDefault());
				break;
			case ImageFormat.GIF:
				dpi = FunctionHelper.applyValue(parameter.gif?.dpi, Gif.getDpiDefault());
				minDpi = Gif.getDpiMin();
				maxDpi = Gif.getDpiMax();
				width = FunctionHelper.applyValue(parameter.gif?.width, Gif.getWidthDefault());
				minWidth = Gif.getWidthMin();
				height = FunctionHelper.applyValue(parameter.gif?.height, Gif.getHeightDefault());
				minHeight = Gif.getHeightMin();
				metrics = FunctionHelper.applyValue(parameter.gif?.metrics, Gif.getMetricsDefault());
				break;
			case ImageFormat.PNG:
				dpi = FunctionHelper.applyValue(parameter.png?.dpi, Png.getDpiDefault());
				minDpi = Png.getDpiMin();
				maxDpi = Png.getDpiMax();
				width = FunctionHelper.applyValue(parameter.png?.width, Png.getWidthDefault());
				minWidth = Png.getWidthMin();
				height = FunctionHelper.applyValue(parameter.png?.height, Png.getHeightDefault());
				minHeight = Png.getHeightMin();
				metrics = FunctionHelper.applyValue(parameter.png?.metrics, Png.getMetricsDefault());
				break;
			case ImageFormat.SVG:
				dpi = FunctionHelper.applyValue(parameter.svg?.dpi, Svg.getDpiDefault());
				minDpi = Svg.getDpiMin();
				maxDpi = Svg.getDpiMax();
				width = FunctionHelper.applyValue(parameter.svg?.width, Svg.getWidthDefault());
				minWidth = Svg.getWidthMin();
				height = FunctionHelper.applyValue(parameter.svg?.height, Svg.getHeightDefault());
				minHeight = Svg.getHeightMin();
				metrics = FunctionHelper.applyValue(parameter.svg?.metrics, Svg.getMetricsDefault());
				break;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ImageExtractionDialogFields.FORMAT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-format">
							<Translate {...this.props} data="extract_images.settings.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-format"
									  value={FunctionHelper.applyValue(
										  parameter?.format, PortalToolboxImageImage.getFormatDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_images.settings.format.jpeg", {ns: "dialog"}
											  ),
											  value: ImageFormat.JPEG
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.format.png", {ns: "dialog"}
											  ),
											  value: ImageFormat.PNG
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.format.gif", {ns: "dialog"}
											  ),
											  value: ImageFormat.GIF
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.format.tiff", {ns: "dialog"}
											  ),
											  value: ImageFormat.TIFF
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.format.bmp", {ns: "dialog"}
											  ),
											  value: ImageFormat.BMP
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.format.svg", {ns: "dialog"}
											  ),
											  value: ImageFormat.SVG
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ImageExtractionDialogFields.FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 parameter?.format !== ImageFormat.SVG ||
							 !this.state.fields?.contains(ImageExtractionDialogFields.SVG_STATE_MODE)
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-state-mode">
							<Translate {...this.props} data="extract_images.settings.svg.state_mode.label"
									   namespace="dialog"/>
							<Info {...this.props} data="extract_images.settings.svg.state_mode.info"
								  options={{ns: "dialog"}} markdown={true}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-state-mode"
									  value={FunctionHelper.applyValue(
										  parameter?.svg?.graphicsStateMode, Svg.getGraphicsStateModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_images.settings.svg.state_mode.verbose",
												  {ns: "dialog"}
											  ),
											  value: SvgGraphicsStateMode.Verbose
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.svg.state_mode.centralize",
												  {ns: "dialog"}
											  ),
											  value: SvgGraphicsStateMode.Centralize
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ImageExtractionDialogFields.SVG_STATE_MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 parameter?.format !== ImageFormat.SVG ||
							 !this.state.fields?.contains(ImageExtractionDialogFields.SVG_PATH_DRAW_MODE)
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-path-draw-mode">
							<Translate {...this.props} data="extract_images.settings.svg.path_draw_mode.label"
									   namespace="dialog"/>
							<Info {...this.props} data="extract_images.settings.svg.path_draw_mode.info"
								  options={{ns: "dialog"}} markdown={true}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-path-draw-mode"
									  value={FunctionHelper.applyValue(
										  parameter?.svg?.pathDrawMode, Svg.getPathDrawModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_images.settings.svg.path_draw_mode.verbose",
												  {ns: "dialog"}
											  ),
											  value: SvgPathDrawMode.Verbose
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.svg.path_draw_mode.compact",
												  {ns: "dialog"}
											  ),
											  value: SvgPathDrawMode.Compact
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ImageExtractionDialogFields.SVG_PATH_DRAW_MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 parameter?.format !== ImageFormat.TIFF ||
							 !this.state.fields?.contains(ImageExtractionDialogFields.COMPRESSION)
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-compression">
							<Translate {...this.props} data="extract_images.settings.tiff.compression.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-compression"
									  value={FunctionHelper.applyValue(
										  parameter?.tiff?.compression, Tiff.getCompressionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.none",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.None
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.ccitt_huffman",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Packbits
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.deflate",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Deflate
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.ccitt_group3",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Group3
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.ccitt_group4",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Group4
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.jpeg",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Jpeg
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.lzw",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Lzw
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.runlength",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Rle
										  },
										  {
											  label: Translate.value(
												  "extract_images.settings.tiff.compression.zlib",
												  {ns: "dialog"}
											  ),
											  value: TiffCompression.Zlib
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ImageExtractionDialogFields.COMPRESSION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 (
								 parameter?.format !== ImageFormat.TIFF ||
								 parameter?.tiff?.compression !== TiffCompression.Jpeg
							 ) &&
							 (
								 parameter?.format !== ImageFormat.JPEG ||
								 !this.state.fields?.contains(ImageExtractionDialogFields.JPEG_QUALITY)
							 )
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-jpeg-quality">
							<Translate {...this.props} data="extract_images.settings.jpeg_quality" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 slider-input">
							<InputNumber value={jpegQuality} min={minJpegQuality} max={maxJpegQuality} step={1}
										 inputId="control-jpeg-quality" suffix=" %" showButtons={true}
										 useGrouping={false}
										 onValueChange={(e) => this.onChange(
											 ImageExtractionDialogFields.JPEG_QUALITY, e.value
										 )}/>
							<Slider value={jpegQuality} min={minJpegQuality} max={maxJpegQuality} step={1}
									onChange={(e) => this.onChange(
										ImageExtractionDialogFields.JPEG_QUALITY, e.value as number
									)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ImageExtractionDialogFields.DPI)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-dpi">
							<Translate {...this.props} data="extract_images.settings.dpi" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber value={dpi} min={minDpi} step={1} max={maxDpi} inputId="control-dpi"
										 suffix={" " + ImageUnit.DPI} showButtons={true} useGrouping={false}
										 onChange={(e) => this.onChange(
											 ImageExtractionDialogFields.DPI, e.value
										 )}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_images.tiff"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<div className="p-col-12 p-grid"
						 hidden={
							 parameter?.format !== ImageFormat.TIFF ||
							 !this.state.fields?.contains(ImageExtractionDialogFields.MULTIPAGE)
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-multipage"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch id="control-multipage"
										 checked={FunctionHelper.applyValue(
											 parameter?.tiff?.multipage, Tiff.getMultipageDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ImageExtractionDialogFields.MULTIPAGE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-multipage">
								<Translate {...this.props} data="extract_images.settings.tiff.multipage.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.extract_images.size"} namespace={"help"}
							   markdown={true}/>
				</Helper>

				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(ImageExtractionDialogFields.METRICS) &&
					!this.state.fields?.contains(ImageExtractionDialogFields.WIDTH) &&
					!this.state.fields?.contains(ImageExtractionDialogFields.HEIGHT)
				}>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ImageExtractionDialogFields.WIDTH)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-width">
							<Translate {...this.props} data="extract_images.size.width" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-width" value={width} min={minWidth} step={1}
										 showButtons={true} useGrouping={false}
										 onChange={(e) => this.onChange(
											 ImageExtractionDialogFields.WIDTH, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ImageExtractionDialogFields.HEIGHT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-height">
							<Translate {...this.props} data="extract_images.size.height" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-height" value={height} min={minHeight} step={1}
										 showButtons={true} useGrouping={false}
										 onChange={(e) => this.onChange(
											 ImageExtractionDialogFields.HEIGHT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ImageExtractionDialogFields.METRICS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-unit">
							<Translate {...this.props} data="extract_images.size.unit" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown value={metrics} inputId="control-unit"
									  options={[
										  {
											  label: Translate.value("inputs.unit.mm"),
											  value: LengthUnit.MILLIMETER
										  },
										  {
											  label: Translate.value("inputs.unit.px"),
											  value: LengthUnit.PIXEL
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ImageExtractionDialogFields.METRICS, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(ImageExtractionDialogFields.FORMAT)}>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ImageExtractionDialogFields.PAGERANGE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="extract_images.pages.label" namespace="dialog"/>
							<Info {...this.props} data="extract_images.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(ImageExtractionDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.pages, ToolboxImageImage.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   ImageExtractionDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={
										   Translate.value("inputs.pagerange.of", {pages: this.state.pages})
									   }
									   tooltip={errors.has(ImageExtractionDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(ImageExtractionDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(ImageExtractionDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(ImageExtractionDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: ImageExtractionDialogFields, value: any): void {
		let parameter: PortalToolboxImageImage = this.state.parameter!.data;
		let errors: Map<ImageExtractionDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case ImageExtractionDialogFields.JPEG_QUALITY:
				parameter.jpeg!.jpegQuality = value;
				parameter.tiff!.jpegQuality = value;
				break;
			case ImageExtractionDialogFields.FORMAT:
				parameter.format = value;
				break;
			case ImageExtractionDialogFields.METRICS:
				parameter.jpeg!.metrics = value;
				parameter.tiff!.metrics = value;
				parameter.bmp!.metrics = value;
				parameter.png!.metrics = value;
				parameter.gif!.metrics = value;
				parameter.svg!.metrics = value;
				break;
			case ImageExtractionDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 0;
				}

				parameter.jpeg!.width = value;
				parameter.tiff!.width = value;
				parameter.bmp!.width = value;
				parameter.png!.width = value;
				parameter.gif!.width = value;
				parameter.svg!.width = value;
				break;
			case ImageExtractionDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 0;
				}

				parameter.jpeg!.height = value;
				parameter.tiff!.height = value;
				parameter.bmp!.height = value;
				parameter.png!.height = value;
				parameter.gif!.height = value;
				parameter.svg!.height = value;
				break;
			case ImageExtractionDialogFields.COMPRESSION:
				parameter.tiff!.compression = value;
				break;
			case ImageExtractionDialogFields.MULTIPAGE:
				parameter.tiff!.multipage = value;
				break;
			case ImageExtractionDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
			case ImageExtractionDialogFields.DPI:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 1;
				}

				parameter.jpeg!.dpi = value;
				parameter.tiff!.dpi = value;
				parameter.bmp!.dpi = value;
				parameter.png!.dpi = value;
				parameter.gif!.dpi = value;
				parameter.svg!.dpi = value;
				break;
			case ImageExtractionDialogFields.SVG_STATE_MODE:
				parameter.svg!.graphicsStateMode = value;
				break;
			case ImageExtractionDialogFields.SVG_PATH_DRAW_MODE:
				parameter.svg!.pathDrawMode = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-image"}/>
					<Translate {...this.props} data="extract_images.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxImageImage | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			currentPage: currentPage,
			pages: pageCount
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let imageExtractionParameter: PortalToolboxImageImage = this.state.parameter!.data.clone();
		switch (imageExtractionParameter.format) {
			case ImageFormat.JPEG:
				imageExtractionParameter.png = undefined;
				imageExtractionParameter.gif = undefined;
				imageExtractionParameter.bmp = undefined;
				imageExtractionParameter.tiff = undefined;
				imageExtractionParameter.svg = undefined;
				break;
			case ImageFormat.PNG:
				imageExtractionParameter.jpeg = undefined;
				imageExtractionParameter.gif = undefined;
				imageExtractionParameter.bmp = undefined;
				imageExtractionParameter.tiff = undefined;
				imageExtractionParameter.svg = undefined;
				break;
			case ImageFormat.GIF:
				imageExtractionParameter.jpeg = undefined;
				imageExtractionParameter.png = undefined;
				imageExtractionParameter.bmp = undefined;
				imageExtractionParameter.tiff = undefined;
				imageExtractionParameter.svg = undefined;
				break;
			case ImageFormat.BMP:
				imageExtractionParameter.jpeg = undefined;
				imageExtractionParameter.png = undefined;
				imageExtractionParameter.gif = undefined;
				imageExtractionParameter.tiff = undefined;
				imageExtractionParameter.svg = undefined;
				break;
			case ImageFormat.TIFF:
				imageExtractionParameter.jpeg = undefined;
				imageExtractionParameter.png = undefined;
				imageExtractionParameter.gif = undefined;
				imageExtractionParameter.bmp = undefined;
				imageExtractionParameter.svg = undefined;
				break;
			case ImageFormat.SVG:
				imageExtractionParameter.jpeg = undefined;
				imageExtractionParameter.png = undefined;
				imageExtractionParameter.gif = undefined;
				imageExtractionParameter.bmp = undefined;
				imageExtractionParameter.tiff = undefined;
				break;
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			image: imageExtractionParameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.extract.image", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EXTRACT_IMAGES;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxImageImage> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const ImageExtractionDialog = React.memo(ImageExtractionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxImageImage>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxImageImage>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ImageExtractionDialog;